export function SecuritySection() {
  return (
    <section id="security" className="relative py-28 px-6 lg:px-8">
      {/* Visual connector line */}
      <div className="sc" aria-hidden="true">
        <svg width="2" height="24" viewBox="0 0 2 24">
          <line x1="1" y1="0" x2="1" y2="24" className="sc-line" stroke="#2563EB" strokeOpacity="0.3" strokeWidth="1.5" />
        </svg>
        <span className="sc-node" />
      </div>

      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-start">
        <div>
          <h2 className="text-[32px] sm:text-[40px] font-extrabold tracking-tight text-ink leading-[1.1] mb-10">
            The questions your regulator will ask.<br />
            We've already answered them.
          </h2>

          <div className="space-y-7">
            <div className="border-l-2 border-line pl-5">
              <div className="text-[14.5px] font-semibold text-ink mb-1.5">
                Audit logs are physically immutable
              </div>
              <p className="text-[13.5px] text-sub leading-relaxed">
                No UPDATE or DELETE is possible on a consent record at the database layer — not by your engineers, not by ours. Append-only is an architectural guarantee, not a policy one.
              </p>
            </div>

            <div className="border-l-2 border-line pl-5">
              <div className="text-[14.5px] font-semibold text-ink mb-1.5">
                Notice version is captured at the moment of consent
              </div>
              <p className="text-[13.5px] text-sub leading-relaxed">
                The SHA hash of the notice shown to the user is written into the consent record at the time of capture. It cannot be retroactively changed. A regulator can verify it independently.
              </p>
            </div>

            <div className="border-l-2 border-line pl-5">
              <div className="text-[14.5px] font-semibold text-ink mb-1.5">
                What happens if Consentry shuts down?
              </div>
              <p className="text-[13.5px] text-sub leading-relaxed">
                All consent records are exportable as portable JSON at any time, from any plan. You own the data. Consentry is the infrastructure — not the keeper. This is a contractual commitment, not a policy.
              </p>
            </div>
          </div>
        </div>

        {/* Audit Timeline Card */}
        <div className="bg-surface border border-line rounded-xl3 p-7">
          <div className="flex items-center justify-between mb-6">
            <div className="text-[13px] font-medium text-ink">Audit timeline</div>
            <span className="mono text-[11px] text-muted">2026-07-14 · Real record</span>
          </div>

          <div className="space-y-5">
            <div className="flex gap-4 items-start">
              <div className="w-8 h-8 rounded-full bg-card border border-line flex items-center justify-center shrink-0 mt-0.5">
                <svg className="w-3.5 h-3.5 text-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 0121 9z" />
                </svg>
              </div>
              <div>
                <div className="text-[13.5px] font-medium text-ink">api_key.created</div>
                <div className="mono text-[11px] text-muted">ck_live_...8f2a · scoped: write:consents</div>
                <div className="mono text-[11px] text-muted">2026-07-14 09:12:04 IST</div>
              </div>
            </div>

            <div className="flex gap-4 items-start">
              <div className="w-8 h-8 rounded-full bg-card border border-line flex items-center justify-center shrink-0 mt-0.5">
                <svg className="w-3.5 h-3.5 text-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <div className="text-[13.5px] font-medium text-ink">notice.published</div>
                <div className="mono text-[11px] text-muted">privacy-v4.2 · sha:a3f9c1b2</div>
                <div className="mono text-[11px] text-muted">2026-07-14 09:20:31 IST</div>
              </div>
            </div>

            <div className="flex gap-4 items-start">
              <div className="w-8 h-8 rounded-full bg-card border border-line flex items-center justify-center shrink-0 mt-0.5">
                <svg className="w-3.5 h-3.5 text-emerald" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <div className="text-[13.5px] font-medium text-ink">consent.granted</div>
                <div className="mono text-[11px] text-muted">dp_usr_48213 · notice sha:a3f9c1b2</div>
                <div className="mono text-[11px] text-muted">2026-07-14 10:03:17 IST</div>
              </div>
            </div>

            <div className="flex gap-4 items-start">
              <div className="w-8 h-8 rounded-full bg-card border border-emerald/30 flex items-center justify-center shrink-0 mt-0.5">
                <svg className="w-3.5 h-3.5 text-emerald" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <div className="text-[13.5px] font-semibold text-ink">certificate.issued</div>
                <div className="mono text-[11px] text-muted">CERT-2026-0091 · Q2 2026</div>
                <div className="mono text-[11px] text-muted">2026-07-14 10:04:09 IST</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
